// src/components/common/ProtectedRoute.tsx

import React from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import { UserRole } from '../../types';
import { ROUTES } from '../../constants';

interface ProtectedRouteProps {
  children?: React.ReactNode;
  allowedRoles?: UserRole[];
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, allowedRoles }) => {
  const { isAuthenticated, user, isLoading } = useAuth();

  // Show loading while checking authentication
  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to={ROUTES.LOGIN} replace />;
  }

  // Check role-based access
  if (allowedRoles && user && !allowedRoles.includes(user.role)) {
    // Redirect to appropriate dashboard based on role
    let redirectPath = ROUTES.RESTAURANT_DASHBOARD;
    if (user.role === UserRole.ADMIN) {
      redirectPath = ROUTES.ADMIN_DASHBOARD;
    } else if (user.role === UserRole.CASHIER || user.role === UserRole.WAITER) {
      redirectPath = ROUTES.RESTAURANT_CASHIER;
    } else if (user.role === UserRole.ACCOUNTANT) {
      redirectPath = ROUTES.RESTAURANT_ACCOUNTANT;
    } else if (user.role === UserRole.DELIVERY_CASHIER) {
      redirectPath = ROUTES.RESTAURANT_CASHIER;
    } else if (user.role === UserRole.INVENTORY_MANAGER) {
      redirectPath = ROUTES.RESTAURANT_INVENTORY;
    }
    return <Navigate to={redirectPath} replace />;
  }

  return <>{children || <Outlet />}</>;
};

export default ProtectedRoute;